import { useState } from 'react';
import { Thermometer, Plus, Loader2, Shield, ChevronDown, Pencil, Trash2, Check, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api/client';
import {
  VITAL_CONFIG, getVitalStatus, vitalLabel, vitalUnit, SOURCE_LABELS,
} from '../utils/vitalConfig';

const VITAL_TYPES = Object.keys(VITAL_CONFIG);

const FIELD = 'w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all appearance-none';

export default function VitalsCard({
  vitals = [],
  canView = true,
  patientId = null,
  isDoctor = false,
  onAdded,
}) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ type: 'blood_pressure', value: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ value: '', notes: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const unit = vitalUnit(form.type);

  const handleAdd = async () => {
    if (!form.value.trim()) { toast.error('Value is required'); return; }
    setSaving(true);
    try {
      const endpoint = isDoctor && patientId
        ? `/patient-profile/${patientId}/vitals`
        : '/patient-profile/me/vitals';
      await api.post(endpoint, { ...form, unit });
      toast.success('Vital recorded');
      setForm({ type: 'blood_pressure', value: '', notes: '' });
      setShowForm(false);
      onAdded?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save vital');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (v) => {
    setEditingId(v._id);
    setEditForm({ value: v.value, notes: v.notes || '' });
  };

  const handleEdit = async (vitalId) => {
    if (!editForm.value.trim()) { toast.error('Value is required'); return; }
    setSavingEdit(true);
    try {
      const endpoint = isDoctor && patientId
        ? `/patient-profile/${patientId}/vitals/${vitalId}`
        : `/patient-profile/me/vitals/${vitalId}`;
      await api.patch(endpoint, editForm);
      toast.success('Vital updated');
      setEditingId(null);
      onAdded?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update vital');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (vitalId) => {
    setDeletingId(vitalId);
    try {
      const endpoint = isDoctor && patientId
        ? `/patient-profile/${patientId}/vitals/${vitalId}`
        : `/patient-profile/me/vitals/${vitalId}`;
      await api.delete(endpoint);
      toast.success('Vital removed');
      onAdded?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete vital');
    } finally {
      setDeletingId(null);
    }
  };

  const displayed = expanded ? vitals : vitals.slice(0, 5);

  return (
    <div className="bg-white rounded-2xl border border-outline-variant/10 shadow-sm p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
            <Thermometer className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="font-headline text-base font-bold text-slate-900">Vitals</h3>
            {canView && (
              <p className="text-xs text-slate-400 font-semibold">{vitals.length} readings</p>
            )}
          </div>
        </div>
        {canView && (
          <button
            onClick={() => setShowForm(v => !v)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-primary/10 text-primary text-xs font-bold hover:bg-primary/20 transition-all"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Reading
          </button>
        )}
      </div>

      {!canView ? (
        <div className="flex items-center gap-2 py-6 justify-center">
          <Shield className="w-4 h-4 text-slate-400" />
          <p className="text-sm text-slate-400">Vitals access not granted</p>
        </div>
      ) : (
        <>
          {/* Add Form */}
          {showForm && (
            <div className="mb-5 p-4 bg-slate-50 rounded-2xl border border-outline-variant/15 space-y-3">
              <p className="text-xs font-extrabold text-primary uppercase tracking-[0.15em]">
                {isDoctor ? 'Record Vital (Clinician)' : 'Add Self-Reported Reading'}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5">Type</label>
                  <div className="relative">
                    <select
                      value={form.type}
                      onChange={e => setForm(f => ({ ...f, type: e.target.value, value: '' }))}
                      className={`${FIELD} pr-9`}
                    >
                      {VITAL_TYPES.map(t => (
                        <option key={t} value={t}>{vitalLabel(t)}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5">
                    Value <span className="normal-case font-normal text-slate-400">({unit})</span>
                  </label>
                  <input
                    type="text"
                    placeholder={form.type === 'blood_pressure' ? '120/80' : '—'}
                    value={form.value}
                    onChange={e => setForm(f => ({ ...f, value: e.target.value }))}
                    className={FIELD}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5">Notes (optional)</label>
                <input
                  type="text"
                  placeholder="e.g. taken after rest, fasting"
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  className={FIELD}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setShowForm(false)} className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 transition-colors">
                  Cancel
                </button>
                <button
                  onClick={handleAdd}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-white text-xs font-bold rounded-xl hover:bg-primary/90 transition-all disabled:opacity-50"
                >
                  {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Save
                </button>
              </div>
            </div>
          )}

          {/* Readings List */}
          {vitals.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">No vital readings recorded</p>
          ) : (
            <div className="space-y-2.5">
              {displayed.map((v, i) => {
                const status = getVitalStatus(v.type, v.value);
                const src = SOURCE_LABELS[v.source] || SOURCE_LABELS.self_reported;
                const cfg = VITAL_CONFIG[v.type];
                const isEditing = editingId === v._id;
                const isDeleting = deletingId === v._id;

                return (
                  <div key={v._id || i} className="p-3.5 bg-slate-50 rounded-xl border border-outline-variant/10 hover:bg-white transition-colors">
                    {isEditing ? (
                      /* Inline edit form */
                      <div className="space-y-2">
                        <p className="text-xs font-bold text-slate-700">{vitalLabel(v.type)}</p>
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="text"
                            value={editForm.value}
                            onChange={e => setEditForm(f => ({ ...f, value: e.target.value }))}
                            placeholder={v.type === 'blood_pressure' ? '120/80' : 'Value'}
                            className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                          />
                          <input
                            type="text"
                            value={editForm.notes}
                            onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                            placeholder="Notes"
                            className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                          />
                        </div>
                        <div className="flex gap-2 justify-end">
                          <button onClick={() => setEditingId(null)} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
                            <X className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(v._id)}
                            disabled={savingEdit}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white text-xs font-bold rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-all"
                          >
                            {savingEdit ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Normal row */
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-bold text-slate-900">{vitalLabel(v.type)}</p>
                            {cfg?.loinc && (
                              <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
                                LOINC {cfg.loinc}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {new Date(v.recordedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            {v.recordedBy?.name && <span className="ml-2">· {v.recordedBy.name}</span>}
                          </p>
                          {v.notes && <p className="text-[11px] text-slate-400 mt-0.5 italic">{v.notes}</p>}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <div className="flex flex-col items-end gap-1.5">
                            <p className="text-base font-extrabold text-slate-900 tabular-nums">
                              {v.value} <span className="text-xs font-semibold text-slate-400">{v.unit || vitalUnit(v.type)}</span>
                            </p>
                            <div className="flex items-center gap-1.5">
                              {status.status !== 'unknown' && (
                                <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full ${status.color}`}>
                                  {status.label}
                                </span>
                              )}
                              <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full ${src.color}`}>
                                {src.label}
                              </span>
                            </div>
                          </div>
                          {/* Edit / Delete actions */}
                          <div className="flex flex-col gap-1 ml-1">
                            <button
                              onClick={() => startEdit(v)}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-primary hover:bg-primary/10 transition-colors"
                              title="Edit"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleDelete(v._id)}
                              disabled={isDeleting}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40"
                              title="Delete"
                            >
                              {isDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {vitals.length > 5 && (
                <button
                  onClick={() => setExpanded(e => !e)}
                  className="w-full text-xs font-bold text-primary hover:underline py-1 transition-colors"
                >
                  {expanded ? 'Show less' : `Show all ${vitals.length} readings`}
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
